import React, { useCallback, useMemo, useRef } from "react";
import styled from "styled-components";
import { useLocation } from "react-router-dom";
import { usePushParams } from "../CurrentMonitoredUri/commons";
import FixtureGate from "../Changes/FixtureGate";
import { colors } from "./colors";
import { addDays, formatDay } from "./format";
import { COMPARES, DEFAULT_COMPARE, DEFAULT_RANGE, RANGES, compareToBaseline, groupChangesByDay, isSameWindow, sliceRange } from "./traffic";
import { DEFAULT_METRIC, METRICS } from "./metrics";
import { useDropsFixture } from "./fixture";
import { useDrops } from "./useDrops";
import { DEFAULT_MEASURE, MEASURES, causeOptionsOf } from "./drops";
import Header from "./Header";
import AlertSummary from "./AlertSummary";
import TrafficChart from "./TrafficChart";
import Table from "./Table";
import Breakdown from "./Breakdown";
import Panel from "./Panel";
import Skeleton from "./Skeleton";

const NO_DROPS = [];

/** `?name=` when it is one of `options`, otherwise the default. */
const paramOf = (params, name, options, fallback) => (options.some((option) => option === params.get(name)) ? params.get(name) : fallback);

/**
 * The drop dashboard (workbench/backlog/feature_watchdog/drop_dashboard.txt) on the workspace's
 * Search Console data. `?range=`, `?compare=` and `?metric=` set the traffic chart, `?measure=` what the
 * drops are read by; the drops list's filters, sort and selected drop are in useDrops.
 */
export default function Drops() {
    const { fixture, error } = useDropsFixture();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const pushParams = usePushParams();
    const tableRef = useRef(null);

    const range = paramOf(params, "range", RANGES.map((option) => option.value), DEFAULT_RANGE);
    const compare = paramOf(params, "compare", COMPARES.map((option) => option.value), DEFAULT_COMPARE);
    const metric = paramOf(params, "metric", METRICS.map((option) => option.key), DEFAULT_METRIC);
    const measure = paramOf(params, "measure", MEASURES.map((option) => option.value), DEFAULT_MEASURE);
    const setParam = (name, fallback) => (value) => pushParams({ [name]: value === fallback ? null : value });

    const drops = useDrops(fixture?.drops ?? NO_DROPS, measure);
    const { items, applyFilters, setSelectedId } = drops;
    const handleClose = useCallback(() => setSelectedId(null), [setSelectedId]);
    const changeDays = useMemo(() => groupChangesByDay(fixture?.pageChangeLog ?? []), [fixture]);

    if (!fixture) return <Page>{error ? <FixtureGate error={error} /> : <Skeleton />}</Page>;

    const { site, trafficSeries, siteEvents, pageChangeLog } = fixture;
    const { current, previous } = sliceRange(trafficSeries, range, compare);
    const weekChange = compareToBaseline(trafficSeries, site.baselineStart);
    const baselineLabel = `${formatDay(site.baselineStart)} – ${formatDay(addDays(site.baselineStart, 6))}`;

    const criticalCount = items.filter((drop) => drop.severity === "critical").length;
    const linkedEvents = [
        ...siteEvents.filter((event) => event.kind === "algorithm")
            .map((event) => ({ title: event.title, count: items.filter((drop) => drop.relatedEventId === event.id).length })),
        { title: "detected page changes", count: items.filter((drop) => drop.pageChanges.length > 0).length },
    ].filter((event) => event.count > 0);

    const scrollToTable = () => tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    const filterAndScroll = (filters) => {
        applyFilters(filters);
        scrollToTable();
    };

    const activeWindow = drops.filters.changeWindow;
    const handleWindowSelect = (window) => {
        if (isSameWindow(window, activeWindow)) drops.updateFilter("changeWindow", null);
        else filterAndScroll({ changeWindow: window });
    };
    const windowEntries = activeWindow ? pageChangeLog.filter((entry) => entry.date >= activeWindow.start && entry.date <= activeWindow.end) : [];

    return (
        <Page>
            <Header site={site} range={range} compare={compare} onRangeChange={setParam("range", DEFAULT_RANGE)} onCompareChange={setParam("compare", DEFAULT_COMPARE)} />

            <AlertSummary changePct={weekChange} baselineLabel={baselineLabel} criticalCount={criticalCount} linkedEvents={linkedEvents}
                          onReview={() => filterAndScroll({ severity: "critical" })} />

            <TrafficChart series={trafficSeries} current={current} previous={previous} range={range} compare={compare}
                          metric={metric} onMetricChange={setParam("metric", DEFAULT_METRIC)} dropWindowStart={site.dropWindowStart}
                          events={siteEvents} changeDays={changeDays} activeWindow={activeWindow} onWindowSelect={handleWindowSelect} />

            <Breakdown fixture={fixture} drops={items} measure={measure}
                       onSectionSelect={(directory) => filterAndScroll({ query: directory })}
                       onCauseSelect={(cause) => filterAndScroll({ cause })} />

            <TableAnchor ref={tableRef} aria-labelledby="drops-heading">
                <Table drops={drops.filtered} measure={measure} onMeasureChange={setParam("measure", DEFAULT_MEASURE)} causeOptions={causeOptionsOf(items)} site={site} changeEntries={windowEntries} total={items.length} filters={drops.filters}
                       severityCounts={drops.severityCounts} hasActiveFilters={drops.hasActiveFilters} sort={drops.sort} selectedId={drops.selectedId}
                       onFilterChange={drops.updateFilter} onResetFilters={drops.resetFilters} onSort={drops.toggleSort} onSelect={setSelectedId} />
            </TableAnchor>

            <Panel drop={drops.selected} measure={measure} site={site} events={siteEvents} hasPrev={drops.hasPrev} hasNext={drops.hasNext} onClose={handleClose}
                   onPrev={() => drops.selectRelative(-1)} onNext={() => drops.selectRelative(1)} />
        </Page>
    );
}

const Page = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    max-width: 1440px;
    margin: 0 auto;
    padding: 24px 0;
    color: ${colors.ink};
    font-feature-settings: "cv11", "ss01";
    -webkit-font-smoothing: antialiased;
`;

const TableAnchor = styled.section`
    scroll-margin-top: 80px;
`;
